<template>
  <div
    ref="modalEl"
    class="modal fade"
    id="viewOpeningBalanceModal"
    tabindex="-1"
  >
    <div class="modal-dialog modal-md modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h5 class="modal-title">
              {{ editMode ? "Edit Opening Balance" : "Opening Balance" }}
            </h5>
            <div v-if="opening" class="text-muted small">
              {{ opening.as_of_period || "—" }}
            </div>
          </div>

          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            :disabled="saving"
          ></button>
        </div>

        <div class="modal-body">
          <div v-if="loading" class="text-center text-muted py-4">
            <i class="bi bi-hourglass-split me-2"></i>Loading…
          </div>

          <div v-else-if="error" class="alert alert-danger">
            {{ error }}
          </div>

          <div v-else-if="!opening" class="alert alert-warning mb-0">
            Opening balance not found for this member.
          </div>

          <div v-else>
            <div class="mb-3">
              <div class="text-muted small">Member</div>
              <div class="fw-semibold">{{ member?.name }}</div>
            </div>

            <!-- VIEW MODE -->
            <div v-if="!editMode">
              <div class="row g-3">
                <div class="col-6">
                  <div class="text-muted small">Amount</div>
                  <div class="fw-bold">
                    {{ fmt(opening.amount) }} RWF
                  </div>
                </div>

                <div class="col-6">
                  <div class="text-muted small">As-of period</div>
                  <div class="fw-semibold">
                    {{ opening.as_of_period || "—" }}
                  </div>
                </div>

                <div class="col-6">
                  <div class="text-muted small">Source</div>
                  <div class="fw-semibold text-capitalize">
                    {{ opening.source_type || "manual" }}
                  </div>
                </div>

                <div class="col-6">
                  <div class="text-muted small">Reference</div>
                  <div class="fw-semibold">
                    {{ opening.source_reference || "—" }}
                  </div>
                </div>
              </div>

              <div v-if="opening.notes || opening.note" class="mt-3">
                <div class="text-muted small">Notes</div>
                <div>{{ opening.notes || opening.note }}</div>
              </div>
            </div>

            <!-- EDIT MODE -->
            <div v-else>
              <div class="alert alert-warning py-2 small">
                You are editing an existing opening balance. This will be saved
                as a <strong>correction</strong>. Please provide a reason.
              </div>

              <div class="row g-3">
                <div class="col-12">
                  <label class="form-label">As-of period</label>
                  <input
                    v-model="form.as_of_period"
                    type="text"
                    class="form-control"
                    disabled
                  />
                  <div class="text-muted small mt-1">
                    Period is locked here. To create another opening period, use
                    the Set Opening Balance modal.
                  </div>
                </div>

                <div class="col-12">
                  <label class="form-label">Amount</label>
                  <input
                    v-model.number="form.amount"
                    type="number"
                    step="0.01"
                    class="form-control"
                    :disabled="saving"
                  />
                  <div class="text-muted small mt-1">
                    Negative values are allowed for carried-forward deficits.
                  </div>
                </div>

                <div class="col-12" v-if="amountDifference !== 0">
                  <div
                    class="border rounded p-2 small"
                    :class="amountDifference > 0 ? 'text-success' : 'text-danger'"
                  >
                    Difference:
                    <strong>
                      {{ amountDifference > 0 ? "+" : "" }}{{ fmt(amountDifference) }} RWF
                    </strong>
                  </div>
                </div>

                <div class="col-12">
                  <label class="form-label">Source reference</label>
                  <input
                    v-model.trim="form.source_reference"
                    type="text"
                    class="form-control"
                    placeholder="e.g. Correction after checking old ledger"
                    :disabled="saving"
                  />
                </div>

                <div class="col-12">
                  <label class="form-label">Correction reason / Notes</label>
                  <textarea
                    v-model.trim="form.notes"
                    class="form-control"
                    rows="3"
                    placeholder="Explain why this opening balance is being corrected..."
                    :disabled="saving"
                  ></textarea>
                  <div class="text-muted small mt-1">
                    Required when saving a correction.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            v-if="opening && !editMode"
            class="btn btn-outline-primary"
            type="button"
            @click="startEdit"
          >
            <i class="bi bi-pencil-square me-1"></i>Edit
          </button>

          <button
            v-if="editMode"
            class="btn btn-outline-secondary"
            type="button"
            :disabled="saving"
            @click="cancelEdit"
          >
            Cancel Edit
          </button>

          <button
            v-if="editMode"
            class="btn btn-primary"
            type="button"
            :disabled="saving || !canSave"
            @click="saveCorrection"
          >
            <span
              v-if="saving"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            {{ saving ? "Saving..." : "Save Correction" }}
          </button>

          <button
            v-if="!editMode"
            class="btn btn-outline-secondary"
            data-bs-dismiss="modal"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/services/http";
import { Modal } from "bootstrap";

export default {
  name: "ViewOpeningBalanceModal",

  emits: ["saved"],

  data() {
    return {
      modal: null,
      loading: false,
      saving: false,
      error: "",
      member: null,
      opening: null,
      editMode: false,

      form: {
        user_id: "",
        as_of_period: "",
        amount: "",
        source_type: "correction",
        source_reference: "",
        notes: "",
      },
    };
  },

  computed: {
    amountDifference() {
      if (!this.opening) return 0;

      const oldAmount = Number(this.opening.amount || 0);
      const newAmount = Number(this.form.amount || 0);

      return Math.round((newAmount - oldAmount) * 100) / 100;
    },

    canSave() {
      if (!this.form.user_id) return false;
      if (!this.form.as_of_period) return false;
      if (this.form.amount === "" || this.form.amount === null) return false;
      if (Number.isNaN(Number(this.form.amount))) return false;

      return String(this.form.notes || "").trim().length >= 3;
    },
  },

  methods: {
    fmt(value) {
      return Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    async open(member) {
      this.member = member;
      this.loading = true;
      this.saving = false;
      this.error = "";
      this.opening = null;
      this.editMode = false;

      this.modal.show();

      try {
        const res = await http.get(`/opening-balances/${member.id}`);
        this.opening = res?.data?.data || null;
      } catch (e) {
        this.error =
          e?.response?.data?.message || "Failed to load opening balance.";
      } finally {
        this.loading = false;
      }
    },

    startEdit() {
      if (!this.opening || !this.member) return;

      this.error = "";
      this.editMode = true;

      this.form = {
        user_id: this.member.id,
        as_of_period: this.opening.as_of_period || "",
        amount: Number(this.opening.amount || 0),
        source_type: "correction",
        source_reference: this.opening.source_reference || "",
        notes: "",
      };
    },

    cancelEdit() {
      this.error = "";
      this.editMode = false;
    },

    async saveCorrection() {
      this.saving = true;
      this.error = "";

      try {
        if (!this.canSave) {
          throw new Error("Please enter a valid amount and correction reason.");
        }

        const payload = {
          user_id: this.form.user_id,
          as_of_period: this.form.as_of_period,
          amount: Number(this.form.amount),
          source_type: "correction",
          source_reference:
            String(this.form.source_reference || "").trim() || null,
          notes: String(this.form.notes || "").trim(),
        };

        const res = await http.post("/opening-balances", payload);

        this.opening = res?.data?.data || null;
        this.editMode = false;

        this.$emit("saved", this.opening);
      } catch (e) {
        if (e instanceof Error && !e?.response) {
          this.error = e.message;
        } else {
          const msg =
            e?.response?.data?.message ||
            "Failed to save opening balance correction.";

          const errs = e?.response?.data?.errors;

          if (errs) {
            const firstKey = Object.keys(errs)[0];
            this.error = errs[firstKey]?.[0] || msg;
          } else {
            this.error = msg;
          }
        }
      } finally {
        this.saving = false;
      }
    },
  },

  mounted() {
    const el = this.$refs.modalEl;
    if (!el) return;

    this.modal = new Modal(el);

    el.addEventListener("hidden.bs.modal", () => {
      this.loading = false;
      this.saving = false;
      this.error = "";
      this.editMode = false;
    });
  },
};
</script>